<template>
  <div class="grid h-full grid-cols-3 divide-x-[3px]">
    <div class="overflow-x-auto">
      <table class="w-full text-sm text-left border-t divide-y divide-gray-200">
        <thead>
          <tr class="divide-x bg-slate-100 text-slate-500">
            <th scope="col" class="p-4 font-medium">{{ $t('salesDocuments.vatCategory') }}</th>
            <th scope="col" class="p-4 font-medium">{{ $t('salesDocuments.tabs.netTotal') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(vatCategory, index) in getVatCategoryValues()"
            :key="index"
            class="text-sm text-gray-900 border-b">
            <th scope="row" class="p-4 font-normal">{{ vatCategory.factor }}</th>
            <td class="px-4 text-right">{{ getDisplayString(vatCategory.totalValue) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="col-span-2">
      <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
        <tbody>
          <BaseDetailsTableRow
            :name="$t('salesDocuments.netTotalBeforeDiscounts')"
            mode="view"
            price
            class="border-t">
            {{ getDisplayString(netTotalBeforeDiscount) ?? '-' }}
          </BaseDetailsTableRow>

          <BaseDetailsTableRow :name="$t('salesDocuments.totalDiscountValue')" mode="view" price>
            {{ getDisplayString(totalDiscounts) }}
          </BaseDetailsTableRow>

          <BaseDetailsTableRow
            :name="$t('salesDocuments.netTotalAfterDiscounts')"
            mode="view"
            price>
            {{ getDisplayString(netTotalAfterDiscount) ?? '-' }}
          </BaseDetailsTableRow>

          <BaseDetailsTableRow :name="$t('salesDocuments.chargesTotals')" mode="view" price>
            {{ getDisplayString(chargesTotal) }}
          </BaseDetailsTableRow>

          <BaseDetailsTableRow :name="$t('salesDocuments.taxesTotal')" mode="view" price>
            {{ getDisplayString(taxesTotal) }}
          </BaseDetailsTableRow>

          <BaseDetailsTableRow :name="$t('salesDocuments.totalVat')" mode="view" price>
            {{ getDisplayString(vatTotal) }}
          </BaseDetailsTableRow>

          <BaseDetailsTableRow
            :name="$t('salesDocuments.grossTotal')"
            mode="view"
            price
            class="font-semibold bg-theme-100">
            {{ getDisplayString(totalSum()) }}
          </BaseDetailsTableRow>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import BaseDetailsTableRow from '@/components/common/BaseDetailsTableRow.vue';
import { useSalesDocumentsStore } from '@/stores/salesDocuments';
import { computed } from 'vue';
import { eChargesType } from '@/types/Documents';
import { roundValue } from '@/utils/prices';
import { RecordMode } from '@/types/Base';

const { modifiedSalesDocument } = storeToRefs(useSalesDocumentsStore());

function getVatCategoryValues(): { factor: number | undefined; totalValue: number | undefined }[] {
  const factorMap = new Map();

  if (
    !modifiedSalesDocument.value ||
    !modifiedSalesDocument.value.documentHeader.documentDetails ||
    !modifiedSalesDocument.value.documentHeader.documentHeaderExpensesAndCharges
  )
    return [];
  const detailVats = modifiedSalesDocument.value.documentHeader.documentDetails
    .filter((d) => d.recordMode !== RecordMode.Deleted)
    .map((x) => ({
      factor: x.vatFactorValue,
      value: x.lineVatTotal,
    }));
  const chargesVats =
    modifiedSalesDocument.value.documentHeader.documentHeaderExpensesAndCharges.map((x) => ({
      factor: x.vatFactorValue,
      value: x.vatValue,
    }));
  const documentVats: { factor: number | undefined; value: number | undefined }[] = [
    ...detailVats,
    ...chargesVats,
  ];

  documentVats.forEach(({ factor, value }) => {
    if (!value) return;
    if (factorMap.has(factor)) {
      factorMap.set(factor, roundValue(factorMap.get(factor) + value));
    } else {
      factorMap.set(factor, value);
    }
  });

  return Array.from(factorMap, ([factor, totalValue]) => ({ factor, totalValue }));
}

const netTotalBeforeDiscount = computed(() => {
  const details = modifiedSalesDocument.value?.documentHeader?.documentDetails?.filter(
    (d) => d.recordMode !== RecordMode.Deleted
  );
  if (!details) return null;
  return roundValue(
    details.reduce((partialNet, x) => partialNet + (x.totalQuantity || x.quantity) * x.unitPrice, 0)
  );
});

const totalDiscounts = computed(() => {
  const details = modifiedSalesDocument.value?.documentHeader?.documentDetails?.filter(
    (d) => d.recordMode !== RecordMode.Deleted
  );
  if (!details) return null;
  const documentLinesDiscounts = details.reduce(
    (partialDiscount, x) => partialDiscount + (x.totalDiscount || 0),
    0
  );
  return roundValue(documentLinesDiscounts);
});

const netTotalAfterDiscount = computed(() => {
  const total =
    [totalDiscounts.value ?? 0, netTotalBeforeDiscount.value ?? 0].reduce(
      (sum, val) => (val || 0) - sum,
      0
    ) ?? 0;

  return roundValue(total);
});

const chargesTotal = computed(() => {
  const expenses = modifiedSalesDocument.value?.documentHeader?.documentHeaderExpensesAndCharges;
  if (!expenses) return null;
  const charges = expenses
    .filter(
      (h) =>
        h.expensesAndCharges.chargeType === eChargesType.Charge ||
        h.expensesAndCharges.chargeType === eChargesType.Expense
    )
    .reduce((partialCharges, x) => partialCharges + (x.grossValue || 0), 0);
  return roundValue(charges);
});

const taxesTotal = computed(() => {
  const expenses = modifiedSalesDocument.value?.documentHeader?.documentHeaderExpensesAndCharges;
  if (!expenses) return null;
  const taxes = expenses
    .filter((h) => h.expensesAndCharges.chargeType === eChargesType.Tax)
    .reduce((partialTaxes, x) => partialTaxes + (x.grossValue || 0), 0);
  return roundValue(taxes);
});

const vatTotal = computed(() => {
  const details = modifiedSalesDocument.value?.documentHeader?.documentDetails?.filter(
    (d) => d.recordMode !== RecordMode.Deleted
  );
  if (!details) return null;
  const vat = details.reduce((partialVat, x) => partialVat + (x.lineVatTotal || 0), 0);
  return roundValue(vat);
});

function totalSum() {
  var gross =
    [
      netTotalAfterDiscount.value ?? 0,
      chargesTotal.value ?? 0,
      taxesTotal.value ?? 0,
      vatTotal.value ?? 0,
    ].reduce((sum, val) => sum + (val || 0), 0) ?? 0;
  return roundValue(gross);
}

function getDisplayString(value: number | null | undefined): string {
  if (value == null) return '';
  return `${value.toFixed(2)} €`;
}
</script>
